import React, { useState, useEffect, useCallback } from 'react';
import { foodDatabaseService } from '../../services/api';
import { Ico, emo } from '../ui/Ico';

const BRAND = '#D4A574';

/* ── Categorias (ordem + ícone + cor) ─────────────────────────────── */
const CATEGORIAS = [
  { nome: 'Cereais e Carboidratos', icon: '🍚', pal: { bg: '#fbf8f5', border: '#D4A574', text: '#9a6a42' } },
  { nome: 'Proteínas Animais',      icon: '🍗', pal: { bg: '#f8ebe9', border: '#c46a60', text: '#a8483f' } },
  { nome: 'Proteínas Vegetais',     icon: '🫛', pal: { bg: '#edf4ee', border: '#46714f', text: '#2f5236' } },
  { nome: 'Leguminosas',            icon: '🫘', pal: { bg: '#fbf8f5', border: '#c4933f', text: '#5a3e2b' } },
  { nome: 'Legumes e Verduras',     icon: '🥦', pal: { bg: '#edf4ee', border: '#46714f', text: '#2f5236' } },
  { nome: 'Frutas',                 icon: '🍎', pal: { bg: '#edf1f6', border: '#7189a6', text: '#34495e' } },
  { nome: 'Laticínios',             icon: '🥛', pal: { bg: '#edf1f6', border: '#4f6a86', text: '#34495e' } },
  { nome: 'Gorduras Boas',          icon: '🥑', pal: { bg: '#f8f1e2', border: '#c4933f', text: '#5e4012' } },
  { nome: 'Bebidas',                icon: '🥤', pal: { bg: '#edf1f6', border: '#7189a6', text: '#34495e' } },
  { nome: 'Outros',                 icon: '🍽️', pal: { bg: '#f6f0ea', border: '#736b64', text: '#4a4440' } },
];
const DEFAULT_PAL = { bg: '#fbf8f5', border: '#a39b93', text: '#4a4440' };
const metaCat = (nome) => CATEGORIAS.find(c => c.nome === nome) || { nome, icon: '🍽️', pal: DEFAULT_PAL };
const ordemCat = (nome) => {
  const i = CATEGORIAS.findIndex(c => c.nome === nome);
  return i === -1 ? 999 : i;
};

const SELO = { TACO: '🇧🇷', USDA: '🇺🇸' };

/* ── Helpers de porção/caloria ────────────────────────────────────── */
const porcaoDe   = (f) => (f.porcao_padrao > 0 ? f.porcao_padrao : 100);
const kcalPorcao = (f) => (f.calorias_100g || 0) * porcaoDe(f) / 100;
const bandaDe    = (kcal) => Math.round(kcal / 50) * 50;   // faixa de 50 em 50 kcal

/* ── Shared styles ────────────────────────────────────────────────── */
const S = {
  input: { width: '100%', padding: '0.55rem 0.85rem', border: '1.5px solid #ebe3da', borderRadius: '9px', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
};

/* ── Macro badges ─────────────────────────────────────────────────── */
function MacroBadges({ prot, carb, fat }) {
  const pill = (label, val, color) => (
    <span key={label} style={{ background: color + '20', color, fontSize: '0.72rem', fontWeight: 600, padding: '2px 7px', borderRadius: '10px', whiteSpace: 'nowrap' }}>
      {emo(label)} {parseFloat(Number(val || 0).toFixed(1))}g
    </span>
  );
  return (
    <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
      {pill('P', prot, '#7189a6')}
      {pill('C', carb, '#b9d2bd')}
      {pill('G', fat,  '#e8c9a8')}
    </div>
  );
}

/* ── Seletor de Tabela Nutricional (controlado pelo pai) ──────────── */
function SeletorTabela({ source, saving, onChange }) {
  return (
    <div style={{ background: '#ffffff', border: '1px solid #ebe3da', borderRadius: '14px', padding: '1rem 1.15rem', marginBottom: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.15rem' }}>
        <span style={{ fontSize: '1.05rem' }}><Ico e="🥗" /></span>
        <h3 style={{ margin: 0, fontSize: '0.98rem', fontWeight: 700, color: '#2f2a26' }}>Tabela Nutricional de Referência</h3>
        {saving && <span style={{ fontSize: '0.72rem', color: '#736b64' }}>atualizando…</span>}
      </div>
      <p style={{ margin: '0 0 0.75rem', fontSize: '0.78rem', color: '#736b64' }}>
        Define de qual base saem os alimentos das substituições e do Banco de Alimentos ao montar o plano.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.6rem' }}>
        {emo([
          { value: 'TACO', label: '🇧🇷 TACO — Tabela Brasileira', desc: 'UNICAMP · Preparações brasileiras' },
          { value: 'USDA', label: '🇺🇸 USDA — FoodData Central', desc: 'Base americana traduzida' },
          { value: 'BOTH', label: '🔀 AMBAS — TACO + USDA', desc: 'Soma das duas tabelas' },
        ].map(opt => {
          const ativo = source === opt.value;
          return (
            <button key={opt.value} onClick={() => onChange(opt.value)} disabled={saving}
              style={{ textAlign: 'left', padding: '0.7rem 0.85rem', borderRadius: '10px', cursor: saving ? 'default' : 'pointer',
                border: `1.5px solid ${ativo ? BRAND : '#d9d0c6'}`, background: ativo ? `${BRAND}14` : '#ffffff' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: ativo ? '#9a6a42' : '#4a4440' }}>
                {emo(opt.label)}{ativo && <span style={{ marginLeft: 6, fontSize: '0.72rem', color: BRAND }}><Ico e="✅" /></span>}
              </div>
              <div style={{ fontSize: '0.72rem', color: '#736b64', marginTop: '2px' }}>{emo(opt.desc)}</div>
            </button>
          );
        }))}
      </div>
    </div>
  );
}

/* ── Uma linha de alimento dentro da faixa ────────────────────────── */
function LinhaAlimento({ f }) {
  const p      = porcaoDe(f);
  const factor = p / 100;
  // porcao_padrao é sempre em gramas; unidade_padrao é a medida caseira ("1 escumadeira").
  const medida = String(f.unidade_padrao || '').trim();
  const temMedida = medida !== '' && medida.toLowerCase() !== 'g';
  return (
    <tr style={{ borderBottom: '1px solid #f6f0ea' }}>
      <td style={{ padding: '0.5rem 0.9rem', fontWeight: 600, color: '#2f2a26' }}>
        <span style={{ marginRight: 6 }}>{emo(SELO[f.source] || '')}</span>{emo(f.nome)}
      </td>
      <td style={{ padding: '0.5rem 0.9rem', color: '#4a4440' }}>
        {Math.round(p)}g{temMedida && <span style={{ color: '#736b64' }}> · {medida}</span>}
      </td>
      <td style={{ padding: '0.5rem 0.9rem', fontWeight: 600, color: BRAND }}>{Math.round(kcalPorcao(f))}</td>
      <td style={{ padding: '0.5rem 0.9rem' }}>
        <MacroBadges prot={(f.proteina_100g || 0) * factor} carb={(f.carbo_100g || 0) * factor} fat={(f.gordura_100g || 0) * factor} />
      </td>
    </tr>
  );
}

/* ── Card de uma categoria (com faixas de caloria) ────────────────── */
function CategoriaCard({ nome, foods, aberto, onToggle }) {
  const meta = metaCat(nome);
  const cor  = meta.pal;

  // agrupa por faixa de 50 kcal (porção padrão) e ordena
  const faixas = {};
  foods.forEach(f => {
    const b = bandaDe(kcalPorcao(f));
    (faixas[b] = faixas[b] || []).push(f);
  });
  const bandasOrdenadas = Object.keys(faixas).map(Number).sort((a, b) => a - b);
  bandasOrdenadas.forEach(b => faixas[b].sort((a, c) => a.nome.localeCompare(c.nome, 'pt')));

  return (
    <div style={{
      border: `1.5px solid ${aberto ? cor.border : cor.border + '70'}`,
      borderRadius: '14px', overflow: 'hidden',
      boxShadow: aberto ? `0 4px 16px ${cor.border}22` : 'none',
    }}>
      <button onClick={onToggle}
        style={{ width: '100%', padding: '1.1rem 1.3rem', background: cor.bg, border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <span style={{ fontSize: '1.6rem', flexShrink: 0 }}>{emo(meta.icon)}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, color: cor.text, fontSize: '0.98rem' }}>{emo(nome)}</div>
          <div style={{ fontSize: '0.8rem', color: '#736b64', marginTop: '0.2rem' }}>
            {bandasOrdenadas.length} faixa{bandasOrdenadas.length !== 1 ? 's' : ''} de caloria · trocas equivalentes
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '1.05rem', color: cor.text }}>{foods.length}</div>
          <div style={{ fontSize: '0.72rem', color: '#736b64' }}>alimentos</div>
        </div>
        <span style={{ color: cor.text, fontSize: '0.8rem', opacity: 0.7 }}>{aberto ? '▲' : '▼'}</span>
      </button>

      {aberto && (
        <div style={{ background: '#ffffff', borderTop: `1px solid ${cor.border}40` }}>
          <div style={{ padding: '0.65rem 1.3rem', background: cor.bg + 'aa', fontSize: '0.79rem', color: cor.text, borderBottom: `1px solid ${cor.border}30` }}>
            <Ico e="💡" sp />Alimentos na mesma faixa de caloria (por porção padrão) são intercambiáveis. Aparecem como opções de troca no plano da paciente.
          </div>
          {bandasOrdenadas.map(b => (
            <div key={b}>
              <div style={{ padding: '0.5rem 1.3rem', background: '#fbf8f5', fontSize: '0.78rem', fontWeight: 700, color: cor.text, borderBottom: `1px solid ${cor.border}20`, display: 'flex', justifyContent: 'space-between' }}>
                <span>≈ {b} kcal por porção</span>
                <span style={{ color: '#736b64', fontWeight: 600 }}>{faixas[b].length} opç{faixas[b].length !== 1 ? 'ões' : 'ão'}</span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
                <thead>
                  <tr style={{ background: '#ffffff' }}>
                    {['Alimento', 'Porção', 'Kcal', 'Macros (P · C · G)'].map(h => (
                      <th key={h} style={{ padding: '0.4rem 0.9rem', textAlign: 'left', fontWeight: 600, color: '#736b64', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {faixas[b].map((f, i) => <LinhaAlimento key={f.id ?? `${b}-${i}`} f={f} />)}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Main component ───────────────────────────────────────────────── */
export default function Substituicoes({ onNavigate }) {
  const [source,  setSource]  = useState('TACO');
  const [saving,  setSaving]  = useState(false);
  const [foods,   setFoods]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [aberta,  setAberta]  = useState(null);

  const carregarFoods = useCallback(async () => {
    setLoading(true);
    try {
      const r = await foodDatabaseService.getAll();
      setFoods((r?.data || r || []).filter(Boolean));
    } catch { setFoods([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    foodDatabaseService.getPreference()
      .then(r => { if (r?.success) setSource(r.data?.preferred_source ?? 'TACO'); })
      .catch(() => {});
    carregarFoods();
  }, [carregarFoods]);

  const trocarTabela = async (novo) => {
    if (novo === source || saving) return;
    setSource(novo);
    setSaving(true);
    try {
      await foodDatabaseService.setPreference(novo);
      await carregarFoods();          // recarrega: a contagem de alimentos muda com a tabela
    } catch { /* mantém UI */ }
    finally { setSaving(false); }
  };

  // agrupa os alimentos por categoria + aplica busca
  const termo = search.trim().toLowerCase();
  const porCategoria = {};
  foods.forEach(f => {
    if (termo && !(`${f.nome} ${f.categoria}`.toLowerCase().includes(termo))) return;
    (porCategoria[f.categoria] = porCategoria[f.categoria] || []).push(f);
  });
  const categorias = Object.keys(porCategoria).sort((a, b) => ordemCat(a) - ordemCat(b) || a.localeCompare(b, 'pt'));

  return (
    <div style={{ maxWidth: '900px' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        {onNavigate && (
          <button onClick={() => onNavigate('configuracoes')}
            style={{ background: 'none', border: 'none', color: '#736b64', cursor: 'pointer', fontSize: '0.85rem', padding: 0, marginBottom: '0.25rem' }}>
            ← Configurações
          </button>
        )}
        <h1 style={{ margin: '0 0 0.25rem', fontSize: '1.5rem', fontWeight: 700, color: '#2f2a26' }}>
          <Ico e="🔄" sp />Substituições de Alimentos
        </h1>
        <p style={{ margin: 0, fontSize: '0.875rem', color: '#736b64' }}>
          Por grupo de alimentos e faixa de caloria — as trocas equivalentes aparecem no plano e no PDF da paciente.
        </p>
      </div>

      <SeletorTabela source={source} saving={saving} onChange={trocarTabela} />

      {/* Busca */}
      <div style={{ position: 'relative', marginBottom: '1.25rem' }}>
        <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', opacity: 0.4 }}><Ico e="🔍" /></span>
        <input style={{ ...S.input, paddingLeft: '2.1rem' }} placeholder="Buscar alimento ou categoria..."
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#736b64' }}>Carregando alimentos…</div>
      ) : categorias.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#736b64' }}>
          {termo ? 'Nenhum alimento encontrado.' : 'Nenhum alimento na tabela selecionada.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          {categorias.map(nome => (
            <CategoriaCard
              key={nome}
              nome={nome}
              foods={porCategoria[nome]}
              aberto={aberta === nome || !!termo}
              onToggle={() => setAberta(aberta === nome ? null : nome)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
